import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Loader2, Sparkles, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { MinnowLogoFull } from '../components/MinnowLogo';

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { fetchUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkPaymentStatus = async () => {
      const sessionId = searchParams.get('session_id');
      if (!sessionId) {
        setError('No session ID found');
        setLoading(false);
        return;
      }

      try {
        // Poll for payment status
        let attempts = 0;
        const maxAttempts = 10;
        
        while (attempts < maxAttempts) {
          const response = await api.getPaymentStatus(sessionId);
          
          if (response.data.payment_status === 'paid') {
            setPaymentData(response.data);
            // Refresh user data to get new tier
            await fetchUser();
            setLoading(false);
            return;
          }
          
          attempts++;
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
        
        setError('Payment verification timeout');
      } catch (err) {
        setError('Failed to verify payment');
      } finally {
        setLoading(false);
      }
    };

    checkPaymentStatus();
  }, [searchParams, fetchUser]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md w-full mx-4 rounded-2xl border-0 shadow-lg">
          <CardContent className="p-8 text-center">
            <Loader2 className="w-12 h-12 animate-spin text-ocean-blue mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">Verifying Payment</h2>
            <p className="text-muted-foreground">Please wait while we confirm your subscription...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md w-full mx-4 rounded-2xl border-0 shadow-lg">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-coral/10 flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">⚠️</span>
            </div>
            <h2 className="text-xl font-semibold text-foreground mb-2">Verification Issue</h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Button onClick={() => navigate('/dashboard')} className="gradient-ocean">
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto px-4 py-16">
        <div className="text-center mb-8">
          <MinnowLogoFull variant="blue" logoSize={48} />
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="rounded-2xl border-0 shadow-lg overflow-hidden">
            <div className="gradient-ocean p-8 text-white text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring" }}
                className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4"
              >
                <CheckCircle className="w-12 h-12" />
              </motion.div>
              <h1 className="text-2xl font-bold mb-2">Welcome to {paymentData?.tier?.charAt(0).toUpperCase() + paymentData?.tier?.slice(1)}!</h1>
              <p className="text-white/80">Your subscription is now active</p>
            </div>

            <CardContent className="p-8">
              <div className="space-y-6">
                <div className="bg-mint/30 rounded-xl p-4">
                  <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-ocean-blue" />
                    What you've unlocked
                  </h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    {paymentData?.tier === 'basic' ? (
                      <>
                        <li>✓ All Layer 0 & 1 modules (57 lessons)</li>
                        <li>✓ 11 hands-on labs</li>
                        <li>✓ Zero-to-One & MVP Builder pathways</li>
                        <li>✓ Regional chat rooms</li>
                        <li>✓ Adaptive weekly plans</li>
                      </>
                    ) : (
                      <>
                        <li>✓ All 65 modules (650 lessons)</li>
                        <li>✓ All 35 labs including Investor Readiness</li>
                        <li>✓ All 7 learning pathways</li>
                        <li>✓ Certification system</li>
                        <li>✓ Priority support</li>
                      </>
                    )}
                  </ul>
                </div>

                <div className="flex gap-4">
                  <Button 
                    onClick={() => navigate('/dashboard')}
                    className="flex-1 gradient-ocean text-white rounded-xl"
                    data-testid="go-to-dashboard-btn"
                  >
                    Go to Dashboard
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  <Button 
                    onClick={() => navigate('/curriculum')}
                    variant="outline"
                    className="flex-1 rounded-xl"
                  >
                    Explore Curriculum
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
